import { useMemo } from "react";
import {
  usePermissionQuery,
  type PermissionResultsByAction,
  type UsePermissionQueryArgs,
  type UsePermissionQueryModel,
} from "@tellescope/tanstack-query";
import type { AccessAction } from "@tellescope/types-models";
import { MODEL_FEATURE_MAP } from "./plan-features";
import { useOrgFeatures } from "./use-org-features";
import type { FeatureAvailability, FeatureName } from "./types";

export interface UseAppPermissionQueryArgs<Model extends UsePermissionQueryModel, Actions extends AccessAction>
  extends UsePermissionQueryArgs<Model, Actions> {
  /** Plan feature gating this check; defaults from `MODEL_FEATURE_MAP[model]`. No feature ⇒ ungated. */
  feature?: FeatureName;
}

export type UseAppPermissionQueryResult<Actions extends AccessAction> = PermissionResultsByAction<Actions> & {
  isLoading: boolean;
  /** The resolved feature availability; `status === "setup-required"` lets call sites render finish-setup UI. */
  feature: FeatureAvailability;
};

const UNGATED: FeatureAvailability = { enabled: true, status: "ready" };

/**
 * App-level permission hook combining plan/feature availability AND traditional
 * record permissions in one answer. The ONLY in-app caller of
 * {@link usePermissionQuery}.
 *
 * Per action, `access` is the AND of the underlying permission's `access` and
 * the resolved feature's `enabled`. The full `feature` availability is also
 * returned so call sites can render upsell / disabled / finish-setup states
 * (when `feature.status === "setup-required"`) instead of hiding UI outright.
 *
 * @param feature Overrides the plan feature gating this check. Defaults from
 *   `MODEL_FEATURE_MAP[model]`; when neither resolves, the check is ungated.
 * @returns Per-action `{ access, type }`, an aggregate `isLoading`, and the
 *   resolved `feature` availability.
 */
export const useAppPermissionQuery = <Model extends UsePermissionQueryModel, Actions extends AccessAction>({
  feature,
  ...args
}: UseAppPermissionQueryArgs<Model, Actions>): UseAppPermissionQueryResult<Actions> => {
  const permissions = usePermissionQuery(args);
  const { getFeatureAvailability, isActionSyncEnabled } = useOrgFeatures();

  const resolvedFeatureName = feature ?? MODEL_FEATURE_MAP[args.model];
  const availability = resolvedFeatureName ? getFeatureAvailability(resolvedFeatureName) : UNGATED;

  return useMemo(() => {
    const gated = args.actions.reduce<PermissionResultsByAction<Actions>>(
      (acc, action) => {
        const base = permissions[action];
        const syncEnabled = action === "read" || isActionSyncEnabled(args.model, action);
        acc[action] = { access: base.access && availability.enabled && syncEnabled, type: base.type };
        return acc;
      },
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      {} as PermissionResultsByAction<Actions>
    );
    return { ...gated, isLoading: permissions.isLoading, feature: availability };
  }, [args.actions, args.model, availability, isActionSyncEnabled, permissions]);
};
